/**
 * Where the composer's controls sit while someone is typing (2026-09-12).
 *
 * Owner report: "the user should have more horizontal space when typing in
 * mobile view (desktop view is fine) so make the send, microphone, and
 * attachment buttons go up vertically in mobile view so as to give more space
 * for an ever growing text draft box."
 *
 * On a phone the three controls sat in a row beside the text box and ate three
 * button-widths of the only dimension that matters there. Two layouts replace
 * it, and the owner chose both halves deliberately:
 *
 *   · SHORT draft  — the controls stack in ONE narrow column at the trailing
 *     edge, so the text box gains roughly two button-widths.
 *   · TALL draft   — they drop to their own row beneath the box, which then
 *     spans the full width.
 *
 * Desktop is explicitly unchanged: the wrapper is `display: contents` there,
 * so the buttons stay direct flex children of the prompt bar and every rule
 * already written for them keeps applying, byte for byte.
 *
 * WHY HYSTERESIS, AND WHY IT IS THE LOAD-BEARING PART.
 *
 * A layout that re-arranges itself while someone is mid-sentence is worse than
 * either layout alone — the owner was told so when she chose this, and
 * accepted it. So the threshold is deliberately NOT a single number: the
 * controls drop below at ENTER lines and return to the column only at EXIT
 * lines, with ENTER > EXIT. With one number, a draft resting exactly on the
 * boundary would flip the whole composer on every other keystroke as a word
 * wrapped and unwrapped. With two, a flip costs a real committed line of text
 * in one direction and a real deleted one in the other.
 *
 * The two threshold functions are PURE so the band can be tested at every line
 * count in both directions rather than eyeballed; the hook below is the only
 * part that needs a DOM. Deliberately no Firebase import of any kind: the
 * 2026-09-11 lesson is that a module pulled into the composer must not drag
 * `firebase/auth` (which touches global fetch at import time) into the
 * chat-isolation suites, which it took down before a single test ran.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Lines of draft at which the controls drop to their own row. */
export const DRAFT_TALL_ENTER_LINES = 3;

/** Lines at or below which they return to the stacked column. */
export const DRAFT_TALL_EXIT_LINES = 2;

/**
 * How many rendered lines the draft occupies — WRAPPED lines, not newlines,
 * which is why this measures the box rather than counting '\n' in the text.
 *
 * Returns 0 for an unmeasurable element (no layout yet, display:none, jsdom
 * without a layout engine) so the caller reads "not tall" rather than
 * flipping on a garbage number.
 */
export function measureDraftLines(el: Pick<HTMLElement, 'getBoundingClientRect'> | null | undefined): number {
  if (!el || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return 0;
  let rect: { height: number };
  try {
    rect = el.getBoundingClientRect();
  } catch {
    return 0;
  }
  if (!rect || !Number.isFinite(rect.height) || rect.height <= 0) return 0;

  const cs = window.getComputedStyle(el as unknown as Element);
  const padding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  const content = rect.height - padding;
  if (!Number.isFinite(content) || content <= 0) return 0;

  // `line-height: normal` parses as NaN, and a font-size-derived fallback is
  // closer than any constant — a composer at 0.9rem and one at 1.1rem do not
  // hit three lines at the same pixel height.
  let lineHeight = parseFloat(cs.lineHeight);
  if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
    const fontSize = parseFloat(cs.fontSize);
    lineHeight = Number.isFinite(fontSize) && fontSize > 0 ? fontSize * 1.4 : 20;
  }

  return Math.max(1, Math.round(content / lineHeight));
}

/**
 * The next tall-state, given the previous one. The whole hysteresis band:
 * rising, it takes ENTER lines to drop the controls below; falling, they come
 * back only once the draft is down to EXIT lines.
 */
export function nextDraftTall(previous: boolean, lines: number): boolean {
  if (!Number.isFinite(lines) || lines <= 0) return false;
  return previous ? lines > DRAFT_TALL_EXIT_LINES : lines >= DRAFT_TALL_ENTER_LINES;
}

/**
 * ONE ROW, AND WHICH CONTROL SITS AT THE END OF IT (2026-09-13).
 *
 * The column above bought horizontal room and spent 100px of vertical to get
 * it — 162px of composer for an empty draft, measured on the deployed build at
 * every phone width. The owner asked for both: more room to type, and the
 * thread not squeezed off the screen.
 *
 * You can only have both by taking a control OUT of the row rather than
 * stacking it. The microphone and the send button are never both useful at the
 * same instant — there is nothing to send until you have written something, and
 * once you have, dictation is not what you reach for — so they share one slot,
 * the way every phone messenger has resolved this. Two 44px controls instead of
 * three 40px ones.
 *
 * PURE, and it mirrors SendButton's own `disabled` expression exactly. That
 * mirroring is the load-bearing part: send is enabled for an attachment-only
 * message, so a predicate that keyed on text alone would show a microphone on a
 * draft that is ready to send and hide the only way to send it.
 *
 * Three rules, in order:
 *   1. No microphone in this browser  → send, always. This is byte-for-byte
 *      today's behaviour wherever dictation is unavailable, which is why the
 *      change cannot strand anyone with no way to send.
 *   2. Dictation running or starting   → microphone. You must always be able to
 *      stop it, and it is the control you just pressed.
 *   3. Otherwise                       → send if there is anything to send,
 *      microphone if the composer is empty.
 *
 * The cost, named: dictate-then-type-then-dictate needs the draft cleared
 * first. That is the price of one row on a 320px screen, where the alternative
 * is a 27px typing area.
 */
export type ComposerTrailingControl = 'send' | 'mic';

export function composerTrailingControl(args: {
  hasDraftText: boolean;
  hasAttachments: boolean;
  dictationAvailable: boolean;
  dictationPhase: string;
}): ComposerTrailingControl {
  if (!args.dictationAvailable) return 'send';
  if (args.dictationPhase !== 'idle') return 'mic';
  return args.hasDraftText || args.hasAttachments ? 'send' : 'mic';
}

/**
 * The rollback lever for the one-row composer.
 *
 * Flip to `false` and the trailing slot carries BOTH controls again, which with
 * the styled column restored is the 2026-09-12 layout. `useDraftTall`,
 * `nextDraftTall`, `measureDraftLines` and both thresholds stay exported and
 * tested precisely so that reversal is a small diff rather than an excavation.
 */
export const COMPOSER_ONE_ROW = true;

/**
 * Watches the draft box and reports whether the controls should have dropped
 * to their own row. Measures the BOX, via ResizeObserver, rather than counting
 * characters — wrapping is what makes a draft tall, and only layout knows
 * where it wrapped.
 *
 * Degrades to `false` wherever it cannot measure (no ResizeObserver, no
 * element, a server render), which is the stacked column — the layout that
 * was already correct before this existed.
 */
export function useDraftTall(
  ref: { current: HTMLElement | null },
  deps: ReadonlyArray<unknown> = [],
): boolean {
  const [tall, setTall] = useState(false);
  const tallRef = useRef(false);

  const sample = useCallback(() => {
    const next = nextDraftTall(tallRef.current, measureDraftLines(ref.current));
    if (next !== tallRef.current) {
      tallRef.current = next;
      setTall(next);
    }
  }, [ref]);

  useEffect(() => {
    sample();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => sample());
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sample, ...deps]);

  return tall;
}
